/**
 * Local (per-browser) persistence for assistant conversations.
 * Deliberately not server-side: the assistant is a single-owner tool and its
 * transcripts are working notes, not shared records.
 */

/**
 * One row in the activity trail: a tool call, or (name === 'note') a line of
 * narration the model wrote between tool calls — kept out of the answer.
 */
export interface AssistantToolEvent {
  id: string;
  name: string;
  input: string;
  status: 'running' | 'done' | 'error';
  summary?: string;
  durationMs?: number;
}

export interface AssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Streamed thinking summary (assistant messages only). */
  thinking?: string;
  toolEvents?: AssistantToolEvent[];
  error?: string;
  stats?: { durationMs: number; rounds: number; outputTokens: number };
  /** Thumbs the person gave this answer (assistant messages only). */
  feedback?: 'UP' | 'DOWN';
  /** The model that produced this answer (assistant messages only). */
  model?: string;
  at: string;
}

export interface AssistantConversation {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: AssistantMessage[];
}

const STORAGE_KEY = 'wms.assistant.v1';
const MAX_CONVERSATIONS = 40;

export function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function titleFromQuestion(question: string): string {
  const compact = question.replace(/\s+/g, ' ').trim();
  return compact.length > 46
    ? `${compact.slice(0, 46)}…`
    : compact || 'New chat';
}

export function loadConversations(): AssistantConversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as {
      conversations?: AssistantConversation[];
    };
    if (!Array.isArray(parsed.conversations)) return [];
    return parsed.conversations;
  } catch {
    return [];
  }
}

export function saveConversations(
  conversations: AssistantConversation[],
): void {
  try {
    const trimmed = [...conversations]
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, MAX_CONVERSATIONS)
      // Never persist an in-flight tool spinner.
      .map((c) => ({
        ...c,
        messages: c.messages.map((m) => ({
          ...m,
          toolEvents: m.toolEvents?.map((t) =>
            t.status === 'running' ? { ...t, status: 'error' as const } : t,
          ),
        })),
      }));
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ conversations: trimmed }),
    );
  } catch {
    // quota/serialization failures are non-fatal — the chat still works
  }
}
